// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * VerifyService - Verificatieprotocol sleutelcode + geboortedatum.
 *
 * Beveiligingsregels (onwijzigbaar):
 * - Max 5 pogingen per apparaat per 24 uur
 * - Identieke foutmelding bij code-fout of DOB-fout
 * - Blokkade telt VOOR verificatie, niet erna
 * - targetUserId wordt pas opgeslagen na match (voorkomt username-enumeration)
 *
 * Huidige hash-aanpak: SHA-256 (geen bcrypt in MVP).
 * STRUCTUUR IS KLAAR voor bcrypt upgrade: vervang sha256Hex door bcryptCompare
 * in de matchKey() helper zodra bcryptjs is geïnstalleerd.
 */
import { db }           from '@/lib/db'
import { generateUUID } from '@/lib/utils'
import type { AccessKey, NextOfKinSession, FailedAttemptRecord } from '@/types'
import { ActivationStatus, SessionRole, SessionStatus } from '@/types'

const MAX_ATTEMPTS   = 5
const BLOCK_HOURS    = 24
const SESSION_HOURS  = 24

// ── Generic error (ALTIJD dezelfde string naar UI) ───────────────────────────
export const VERIFY_FAILED_MESSAGE =
  'De sleutelcode of geboortedatum is onjuist. Controleer uw invoer en probeer het opnieuw.'

export const VERIFY_BLOCKED_MESSAGE =
  'Toegang tijdelijk geblokkeerd wegens te veel mislukte pogingen.'

// ── Device fingerprint (lichtgewicht, geen tracking) ─────────────────────────
function getDeviceFingerprint(): string {
  const parts = [
    navigator.userAgent,
    navigator.language,
    String(screen.colorDepth),
    String(screen.width),
  ]
  return parts.join('|').slice(0, 200)
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

async function pbkdf2Hex(input: string, salt: string): Promise<string> {
  const enc = new TextEncoder()
  const km  = await crypto.subtle.importKey('raw', enc.encode(input), 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: enc.encode(salt), iterations: 310_000 },
    km,
    256,
  )
  return Array.from(new Uint8Array(bits)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function normalizeCode(input: string): string {
  return input.replace(/[-–\s]/g, '').toUpperCase().trim()
}

// ── Blokkade-check ─────────────────────────────────────────────────────────────

export interface BlockStatus {
  blocked:       boolean
  blockedUntil:  string | null
  remainingMs:   number
  attemptNumber: number
}

export async function checkBlockStatus(): Promise<BlockStatus> {
  const fingerprint = getDeviceFingerprint()
  const now = new Date()

  // Haal alle records op voor dit apparaat, gesorteerd op meest recent
  const records = await db.failed_attempts
    .where('deviceFingerprint')
    .equals(fingerprint)
    .toArray()

  if (records.length === 0) return { blocked: false, blockedUntil: null, remainingMs: 0, attemptNumber: 0 }

  // Zoek actief blokkade-record
  const blocked = records.find(r => r.blockedUntil && new Date(r.blockedUntil) > now)
  if (blocked?.blockedUntil) {
    const blockedUntil = new Date(blocked.blockedUntil)
    return {
      blocked:      true,
      blockedUntil: blocked.blockedUntil,
      remainingMs:  blockedUntil.getTime() - now.getTime(),
      attemptNumber: blocked.attemptNumber,
    }
  }

  // Pogingen in afgelopen 24u tellen
  const windowStart = new Date(now.getTime() - BLOCK_HOURS * 60 * 60 * 1000)
  const recentCount = records.filter(r => new Date(r.attemptedAt) > windowStart).length

  return { blocked: false, blockedUntil: null, remainingMs: 0, attemptNumber: recentCount }
}

// ── Poging registreren ────────────────────────────────────────────────────────

async function recordFailedAttempt(inputCode: string): Promise<BlockStatus> {
  const fingerprint = getDeviceFingerprint()
  const now         = new Date()
  const windowStart = new Date(now.getTime() - BLOCK_HOURS * 60 * 60 * 1000)

  // Tel huidige pogingen in window
  const recent = await db.failed_attempts
    .where('deviceFingerprint')
    .equals(fingerprint)
    .filter(r => new Date(r.attemptedAt) > windowStart)
    .toArray()

  const attemptNumber = recent.length + 1
  const shouldBlock   = attemptNumber >= MAX_ATTEMPTS
  const blockedUntil  = shouldBlock
    ? new Date(now.getTime() + BLOCK_HOURS * 60 * 60 * 1000).toISOString()
    : null

  const inputCodeHash = await sha256Hex(inputCode.slice(0, 20)) // hash van invoer voor audit

  const record: FailedAttemptRecord = {
    id:                generateUUID(),
    targetUserId:      null,  // bewust null - voorkomt username-enumeration
    inputCodeHash,
    attemptedAt:       now.toISOString(),
    ipRegion:          null,
    deviceFingerprint: fingerprint,
    attemptNumber,
    blockedUntil,
    blockReason:       shouldBlock ? 'TOO_MANY_ATTEMPTS' : null,
    alertSentToOwner:  false,
  }

  await db.failed_attempts.add(record)

  return {
    blocked:      shouldBlock,
    blockedUntil: blockedUntil,
    remainingMs:  shouldBlock ? BLOCK_HOURS * 60 * 60 * 1000 : 0,
    attemptNumber,
  }
}

// ── Match-helpers (bcrypt-klaar) ──────────────────────────────────────────────

/**
 * Vergelijkt een invoercode met een opgeslagen hash.
 * MVP: SHA-256. Vervang dit door bcryptjs.compare() voor bcrypt upgrade.
 */
async function matchCodeHash(normalizedInput: string, storedHash: string, userId: string): Promise<boolean> {
  const inputHash = await sha256Hex(normalizedInput + userId)
  // Constant-time vergelijking: altijd alle bytes vergelijken
  if (inputHash.length !== storedHash.length) return false
  let diff = 0
  for (let i = 0; i < inputHash.length; i++) {
    diff |= inputHash.charCodeAt(i) ^ storedHash.charCodeAt(i)
  }
  return diff === 0
}

/**
 * Vergelijkt een geboortedatum met de opgeslagen PBKDF2-hash.
 */
async function matchDobHash(dob: string, userId: string, storedHash: string): Promise<boolean> {
  const inputHash = await pbkdf2Hex(dob, userId)
  if (inputHash.length !== storedHash.length) return false
  let diff = 0
  for (let i = 0; i < inputHash.length; i++) {
    diff |= inputHash.charCodeAt(i) ^ storedHash.charCodeAt(i)
  }
  return diff === 0
}

// ── Verificatie ───────────────────────────────────────────────────────────────

export interface VerifyResult {
  success:      boolean
  sessionToken: string | null
  userId:       string | null
  expiresAt:    string | null
  errorMessage: string | null
  blocked:      boolean
  blockedUntil: string | null
  remainingMs:  number
  attemptNumber: number
}

export async function verifyAndCreateSession(
  rawCode: string,
  dateOfBirth: string,
): Promise<VerifyResult> {
  const fail = (blocked = false, status?: BlockStatus): VerifyResult => ({
    success: false, sessionToken: null, userId: null, expiresAt: null,
    errorMessage: blocked ? VERIFY_BLOCKED_MESSAGE : VERIFY_FAILED_MESSAGE,
    blocked, blockedUntil: status?.blockedUntil ?? null,
    remainingMs: status?.remainingMs ?? 0,
    attemptNumber: status?.attemptNumber ?? 0,
  })

  // Stap 1: blokkade-check VOOR verificatie
  const blockStatus = await checkBlockStatus()
  if (blockStatus.blocked) return fail(true, blockStatus)

  const normalizedCode = normalizeCode(rawCode)
  if (normalizedCode.length !== 8) {
    const s = await recordFailedAttempt(rawCode)
    return fail(s.blocked, s)
  }

  // Stap 2: haal ALLE actieve AccessKey records op (geen userId-filter)
  const allKeys = await db.access_keys
    .where('activationStatus')
    .equals(ActivationStatus.ACTIVE)
    .toArray()

  let matchedKey: AccessKey | null = null

  // Stap 3: constant-time iteratie over alle keys (altijd alles doorlopen)
  for (const key of allKeys) {
    const codeMatch = await matchCodeHash(normalizedCode, key.codeHash, key.userId)
    if (codeMatch && matchedKey === null) {
      matchedKey = key // eerste match onthouden, maar ALTIJD doorgaan
    }
  }

  // Stap 4: geen match - poging registreren
  if (!matchedKey) {
    const s = await recordFailedAttempt(rawCode)
    return fail(s.blocked, s)
  }

  // Stap 5: DOB verificatie (apart, constant-time)
  const dobMatch = await matchDobHash(dateOfBirth, matchedKey.userId, matchedKey.dobHash)
  if (!dobMatch) {
    const s = await recordFailedAttempt(rawCode)
    return fail(s.blocked, s)
  }

  // Stap 6: sessie aanmaken
  const sessionToken = generateUUID()
  const now          = new Date()
  const expiresAt    = new Date(now.getTime() + SESSION_HOURS * 60 * 60 * 1000).toISOString()

  const session: NextOfKinSession = {
    id:                       sessionToken,
    accessKeyId:              matchedKey.id,
    userId:                   matchedKey.userId,
    sessionRole:              SessionRole.TRUSTED_PERSON,
    createdAt:                now.toISOString(),
    expiresAt,
    lastActivityAt:           now.toISOString(),
    extendedAt:               null,
    extendedUntil:            null,
    deviceTokenHash:          null,
    correctionWindowOpen:     false,
    correctionWindowClosesAt: null,
    status:                   SessionStatus.ACTIVE,
    revokedAt:                null,
  }

  await db.nextofkin_sessions.add(session)

  return {
    success: true,
    sessionToken,
    userId:       matchedKey.userId,
    expiresAt,
    errorMessage: null,
    blocked:      false,
    blockedUntil: null,
    remainingMs:  0,
    attemptNumber: 0,
  }
}
